import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { dismissAlert, resolveAlert } from '@/store/slices/alertsSlice';
import { alertService } from '@/services/alertService';
import {
  getAlertDisplayName,
  getAlertDescription,
  getAgentDisplayName,
} from '@/utils/alertTypes';
import type { AlertSeverity } from '@/types/alerts';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Pill,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@gapartners/ui';

export function AlertsView() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { activeAlerts, resolvedAlerts, isConnected, error, lastUpdated } =
    useAppSelector(state => state.alerts);
  const [resolvingById, setResolvingById] = useState<Record<string, boolean>>(
    {}
  );
  const [actionError, setActionError] = useState<string | null>(null);

  // Set page metadata
  usePageMetadata({
    title: t('pages.manager.alerts.title'),
    description: t('pages.manager.alerts.description'),
  });

  // Sort alerts by timestamp (newest first)
  const sortedActiveAlerts = useMemo(
    () =>
      [...activeAlerts].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    [activeAlerts]
  );

  const getSeverityColor = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical':
        return '#DC2626'; // red
      case 'high':
        return '#EA580C'; // orange
      case 'medium':
        return '#EABB08'; // yellow
      case 'low':
        return '#3B82F6'; // blue
      default:
        return '#6B7280'; // gray
    }
  };

  const getSeverityLabel = (severity: AlertSeverity) => {
    return t(`pages.manager.alerts.severity.${severity}`);
  };

  const formatTimestamp = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return isoString;
    }
  };

  const handleResolveAlert = useCallback(
    async (alertId: string) => {
      setActionError(null);
      setResolvingById(prev => ({ ...prev, [alertId]: true }));
      try {
        await alertService.requestResolveAlert(alertId);
        dispatch(resolveAlert(alertId));
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('pages.manager.alerts.resolveFailed');
        setActionError(message);
      } finally {
        setResolvingById(prev => {
          const next = { ...prev };
          delete next[alertId];
          return next;
        });
      }
    },
    [dispatch, t]
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            color="text.primary"
            gutterBottom
          >
            {t('pages.manager.alerts.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages.manager.alerts.description')}
          </Typography>
        </Box>

        {/* Connection Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: isConnected ? '#10B981' : '#EF4444',
              animation: isConnected ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {isConnected
              ? t('pages.manager.alerts.connected')
              : t('pages.manager.alerts.disconnected')}
          </Typography>
          {lastUpdated && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 'auto' }}
            >
              {t('pages.manager.alerts.lastUpdated')}{' '}
              {formatTimestamp(lastUpdated)}
            </Typography>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Box
            sx={{
              p: 2,
              bgcolor: '#FEE2E2',
              border: '1px solid #FECACA',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="#991B1B">
              {error}
            </Typography>
          </Box>
        )}

        {actionError && (
          <Box
            sx={{
              p: 2,
              bgcolor: '#FEE2E2',
              border: '1px solid #FECACA',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="#991B1B">
              {actionError}
            </Typography>
          </Box>
        )}

        {/* Active Alerts Section */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.primary"
            sx={{ mb: 1.5 }}
          >
            {t('pages.manager.alerts.activeAlerts')} ({activeAlerts.length})
          </Typography>

          {sortedActiveAlerts.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('pages.manager.alerts.noActiveAlerts')}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.severity')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.type')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.agent')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.queue')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.message')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.description')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.time')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedActiveAlerts.map(
                    (alert: (typeof sortedActiveAlerts)[0]) => (
                      <TableRow key={alert.id} hover>
                        <TableCell>
                          <Pill
                            label={getSeverityLabel(alert.severity)}
                            size="small"
                            variant="filled"
                            sx={{
                              bgcolor: getSeverityColor(alert.severity),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{alert.type}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getAgentDisplayName(alert)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {alert.queueName ?? '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getAlertDisplayName(alert)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {getAlertDescription(alert)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(alert.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <Tooltip title={t('pages.manager.alerts.resolve')}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!!resolvingById[alert.id]}
                                  onClick={() =>
                                    void handleResolveAlert(alert.id)
                                  }
                                  aria-label={t('pages.manager.alerts.resolve')}
                                >
                                  {resolvingById[alert.id] ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('common.close')}>
                              <IconButton
                                size="small"
                                disabled={!!resolvingById[alert.id]}
                                onClick={() => dispatch(dismissAlert(alert.id))}
                                aria-label={t('common.close')}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Recently Resolved Section */}
        {resolvedAlerts.length > 0 && (
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 1.5 }}
            >
              {t('pages.manager.alerts.recentlyResolved')} (
              {resolvedAlerts.length})
            </Typography>

            <TableContainer
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.severity')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.type')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.agent')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.queue')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.message')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.description')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('pages.manager.alerts.table.time')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resolvedAlerts.map((alert: (typeof resolvedAlerts)[0]) => (
                    <TableRow key={alert.id} hover sx={{ opacity: 0.6 }}>
                      <TableCell>
                        <Pill
                          label={getSeverityLabel(alert.severity)}
                          size="small"
                          variant="filled"
                          sx={{
                            bgcolor: getSeverityColor(alert.severity),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{alert.type}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getAgentDisplayName(alert)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {alert.queueName ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getAlertDisplayName(alert)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getAlertDescription(alert)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(alert.timestamp)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
